'use client';

import { useEffect } from 'react';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { AisLink as Link } from '@/components/aisearch/ais-link';
import { RotateCw } from 'lucide-react';
import { NeuralPlayground } from '@/components/ui/neural-playground';

/**
 * Ошибка на домене AiSearch. Лежит внутри ветки `aisearch`, поэтому
 * наследует её тему, шапку и подвал — см. `not-found.tsx` рядом.
 *
 * `useTranslations` здесь читает локаль из `NextIntlClientProvider`,
 * который настраивается в `[locale]/layout.tsx` через `requestLocale`.
 * На домене продукта этот механизм не задействован middleware'ом
 * next-intl (см. комментарий в `not-found.tsx`), поэтому текст рядом
 * может молча остаться английским, даже когда адрес на `/ru/...`.
 * `useParams()` достаёт локаль из сегмента маршрута напрямую, в обход
 * этого пробела: Error Boundary не получает `params` пропом, в отличие
 * от обычной страницы.
 */
export default function AiSearchError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const params = useParams<{ locale?: string }>();
  const t = useTranslations('ais_errors');

  useEffect(() => {
    console.error('Ошибка страницы AiSearch:', error.digest ?? error.message, params.locale);
  }, [error, params.locale]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-20 text-center relative overflow-hidden">
      <div className="relative w-full max-w-3xl h-[320px] sm:h-[380px] mb-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden">
        <NeuralPlayground className="absolute inset-0 w-full h-full" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className="text-7xl sm:text-8xl font-bold text-[var(--color-text)]/90 leading-none">
            500
          </p>
        </div>
      </div>

      <p className="text-xs text-[var(--color-text-muted)] mb-8">{t('hint')}</p>

      <h1 className="text-2xl font-bold mb-3">{t('error_title')}</h1>
      <p className="text-[var(--color-text-muted)] mb-8 max-w-md leading-relaxed">
        {t('error_desc')}
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={reset}
          className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-xl bg-[var(--color-accent-500)] hover:bg-[var(--color-accent-400)] text-[var(--color-on-brand)] font-semibold transition-colors"
        >
          <RotateCw className="w-4 h-4" />
          {t('retry')}
        </button>
        <Link
          href="/"
          className="px-7 py-3 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-accent-500)] bg-[var(--color-bg-card)] font-semibold transition-colors"
        >
          {t('home')}
        </Link>
      </div>

      {error.digest && (
        <p className="mt-10 text-xs text-[var(--color-text-muted)]/70">
          ID: <code className="font-mono">{error.digest}</code>
        </p>
      )}
    </div>
  );
}
